(() => {
  if (customElements.get('article-carousel')) return;

  class ArticleCarousel extends HTMLElement {
    constructor() {
      super();
      this._abort = null;
      this._autoplayTimer = null;
      this._raf = null;
      this._slideOffsets = [];
      this._isDragging = false;
      this._dragStartX = 0;
      this._dragStartScrollLeft = 0;
      this._currentIndex = 0;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      const track = this.querySelector('[data-article-carousel-track]');
      if (!track) return;

      this._abort = new AbortController();
      this._track = track;
      this._slides = Array.from(this.querySelectorAll('[data-article-carousel-slide]'));
      this._dots = Array.from(this.querySelectorAll('[data-article-carousel-dot]'));
      this._prevButton = this.querySelector('[data-article-carousel-prev]');
      this._nextButton = this.querySelector('[data-article-carousel-next]');

      this._loop = this.dataset.loop === 'true';
      this._autoplay = this.dataset.autoplay === 'true';
      this._intervalMs = Math.max(parseInt(this.dataset.interval || '5000', 10) || 5000, 1000);

      this._computeOffsets();
      this._syncUI();

      this._prevButton?.addEventListener(
        'click',
        () => this._goPrev(),
        { signal: this._abort.signal }
      );
      this._nextButton?.addEventListener(
        'click',
        () => this._goNext(),
        { signal: this._abort.signal }
      );

      this._dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            const index = parseInt(dot.dataset.index || '0', 10) || 0;
            this.goTo(index);
          },
          { signal: this._abort.signal }
        );
      });

      // Scroll → update current index (debounced via rAF).
      this._track.addEventListener(
        'scroll',
        () => {
          if (this._raf) cancelAnimationFrame(this._raf);
          this._raf = requestAnimationFrame(() => {
            this._currentIndex = this._nearestIndex();
            this._syncUI();
          });
        },
        { passive: true, signal: this._abort.signal }
      );

      // Drag/swipe support (pointer events).
      this._track.addEventListener(
        'pointerdown',
        (e) => this._onPointerDown(e),
        { signal: this._abort.signal }
      );
      this._track.addEventListener(
        'pointermove',
        (e) => this._onPointerMove(e),
        { signal: this._abort.signal }
      );
      this._track.addEventListener(
        'pointerup',
        () => this._onPointerUp(),
        { signal: this._abort.signal }
      );
      this._track.addEventListener(
        'pointercancel',
        () => this._onPointerUp(),
        { signal: this._abort.signal }
      );

      // Keyboard support (when focused within).
      this.addEventListener(
        'keydown',
        (e) => this._onKeydown(e),
        { signal: this._abort.signal }
      );

      // Autoplay pause/resume.
      this.addEventListener(
        'mouseenter',
        () => this._stopAutoplay(),
        { signal: this._abort.signal }
      );
      this.addEventListener(
        'mouseleave',
        () => this._startAutoplay(),
        { signal: this._abort.signal }
      );
      this.addEventListener(
        'focusin',
        () => this._stopAutoplay(),
        { signal: this._abort.signal }
      );
      this.addEventListener(
        'focusout',
        () => this._startAutoplay(),
        { signal: this._abort.signal }
      );

      window.addEventListener(
        'resize',
        () => {
          this._computeOffsets();
          this.goTo(this._currentIndex, { behavior: 'auto' });
        },
        { signal: this._abort.signal }
      );

      this._startAutoplay();
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = null;
      this._abort?.abort();
      this._abort = null;
      this._track = null;
      this._slides = null;
      this._dots = null;
      this._prevButton = null;
      this._nextButton = null;
      this.dataset.initialized = 'false';
    }

    _computeOffsets() {
      this._slideOffsets = (this._slides || []).map((slide) => slide.offsetLeft);
    }

    _nearestIndex() {
      if (!this._track || this._slideOffsets.length === 0) return 0;
      const x = this._track.scrollLeft;
      let bestIndex = 0;
      let bestDist = Infinity;
      for (let i = 0; i < this._slideOffsets.length; i += 1) {
        const dist = Math.abs(this._slideOffsets[i] - x);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
        }
      }
      return bestIndex;
    }

    _clampIndex(index) {
      const max = (this._slides?.length || 0) - 1;
      if (max < 0) return 0;
      if (this._loop) {
        if (index < 0) return max;
        if (index > max) return 0;
        return index;
      }
      return Math.max(0, Math.min(index, max));
    }

    goTo(index, { behavior = 'smooth' } = {}) {
      if (!this._track) return;
      const nextIndex = this._clampIndex(index);
      const left = this._slideOffsets[nextIndex] ?? 0;
      this._track.scrollTo({ left, behavior });
      this._currentIndex = nextIndex;
      this._syncUI();
    }

    _goPrev() {
      this.goTo(this._currentIndex - 1);
    }

    _goNext() {
      this.goTo(this._currentIndex + 1);
    }

    _syncUI() {
      const count = this._slides?.length || 0;
      if (count <= 1) return;

      if (this._prevButton) this._prevButton.disabled = !this._loop && this._currentIndex <= 0;
      if (this._nextButton)
        this._nextButton.disabled = !this._loop && this._currentIndex >= count - 1;

      this._dots?.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === this._currentIndex ? 'true' : 'false');
      });
    }

    _startAutoplay() {
      if (!this._autoplay) return;
      if ((this._slides?.length || 0) <= 1) return;
      if (this._autoplayTimer) return;
      this._autoplayTimer = window.setInterval(() => {
        this._goNext();
      }, this._intervalMs);
    }

    _stopAutoplay() {
      if (!this._autoplayTimer) return;
      window.clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }

    _onPointerDown(e) {
      if (!this._track) return;
      // Only primary button / touch.
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this._isDragging = true;
      this._dragStartX = e.clientX;
      this._dragStartScrollLeft = this._track.scrollLeft;
      this._track.setPointerCapture?.(e.pointerId);
      this._stopAutoplay();
    }

    _onPointerMove(e) {
      if (!this._track || !this._isDragging) return;
      const dx = e.clientX - this._dragStartX;
      this._track.scrollLeft = this._dragStartScrollLeft - dx;
    }

    _onPointerUp() {
      if (!this._track || !this._isDragging) return;
      this._isDragging = false;
      const snapIndex = this._nearestIndex();
      this.goTo(snapIndex);
      this._startAutoplay();
    }

    _onKeydown(e) {
      if (e.defaultPrevented) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this._goPrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this._goNext();
          break;
        case 'Home':
          e.preventDefault();
          this.goTo(0);
          break;
        case 'End':
          e.preventDefault();
          this.goTo((this._slides?.length || 1) - 1);
          break;
        default:
          break;
      }
    }
  }

  customElements.define('article-carousel', ArticleCarousel);
})();

